import {CART_ITEM_ADDED, CART_ITEM_CHANGED} from '@core/scripts/components/cart-events';
import {sendDyEvent} from '@dynamic-yield/scripts/components/dynamic-yield/dy-event-sender';
import {
    DyEventName,
    DyEventType,
    toDyPrice,
    toDyProductId,
} from '@dynamic-yield/scripts/components/dynamic-yield/dy-event-types';

type CartItemAddedDetail = {
    itemOptionId?: string | number | null;
    quantity?: number;
    price?: string | number | null;
};

type CartItemChangedDetail = {
    previousItem?: {
        itemOptionId?: string | number | null;
        quantity?: number;
        singlePrice?: string | number | null;
    };
    newItem?: {
        itemOptionId?: string | number | null;
        quantity?: number;
        singlePrice?: string | number | null;
    };
};

type CartState = {
    productId: string;
    quantity: number;
    unitPrice: number | null;
};

type CartMutation = {
    name: string;
    dyType: string;
    productId: string;
    quantity: number;
    unitPrice: number | null;
};

export type DyCartEventOptions = {
    currency?: string;
};

export function observeDyCartEvents({currency}: DyCartEventOptions = {}): () => void {
    const onAdded = (event: Event) => {
        onCartItemAdded((event as CustomEvent<CartItemAddedDetail>).detail ?? {}, currency);
    };
    const onChanged = (event: Event) => {
        onCartItemChanged((event as CustomEvent<CartItemChangedDetail>).detail ?? {}, currency);
    };

    document.addEventListener(CART_ITEM_ADDED, onAdded);
    document.addEventListener(CART_ITEM_CHANGED, onChanged);

    return () => {
        document.removeEventListener(CART_ITEM_ADDED, onAdded);
        document.removeEventListener(CART_ITEM_CHANGED, onChanged);
    };
}

function onCartItemAdded(detail: CartItemAddedDetail, currency?: string): void {
    const productId = toDyProductId(detail);
    const quantity = Number(detail.quantity ?? 0);

    if (productId === null || quantity <= 0) {
        return;
    }

    send(
        {
            name: DyEventName.ADD_TO_CART,
            dyType: DyEventType.ADD_TO_CART,
            productId,
            quantity,
            unitPrice: toDyPrice(detail.price),
        },
        currency,
    );
}

function onCartItemChanged(detail: CartItemChangedDetail, currency?: string): void {
    const previousProductId = toDyProductId({itemOptionId: detail.previousItem?.itemOptionId});
    const previousQuantity = Number(detail.previousItem?.quantity ?? 0);
    const previousPrice = toDyPrice(detail.previousItem?.singlePrice);

    if (previousProductId === null || previousQuantity <= 0) {
        return;
    }

    const previous = {productId: previousProductId, quantity: previousQuantity, unitPrice: previousPrice};
    const current = {
        productId: toDyProductId({itemOptionId: detail.newItem?.itemOptionId}) ?? previousProductId,
        quantity: Number(detail.newItem?.quantity ?? 0),
        unitPrice: toDyPrice(detail.newItem?.singlePrice) ?? previousPrice,
    };

    cartMutationsOf(previous, current).forEach((mutation) => send(mutation, currency));
}

function cartMutationsOf(previous: CartState, current: CartState): CartMutation[] {
    const removal = (quantity: number) => ({
        name: DyEventName.REMOVE_FROM_CART,
        dyType: DyEventType.REMOVE_FROM_CART,
        productId: previous.productId,
        quantity,
        unitPrice: previous.unitPrice,
    });
    const addition = (quantity: number) => ({
        name: DyEventName.ADD_TO_CART,
        dyType: DyEventType.ADD_TO_CART,
        productId: current.productId,
        quantity,
        unitPrice: current.unitPrice,
    });

    if (current.quantity < previous.quantity) {
        return [removal(previous.quantity - current.quantity)];
    }

    if (current.quantity > previous.quantity) {
        return [addition(current.quantity - previous.quantity)];
    }

    if (current.productId !== previous.productId) {
        return [removal(previous.quantity), addition(previous.quantity)];
    }

    return [];
}

function send(mutation: CartMutation, currency: string | undefined): void {
    const {name, dyType, productId, quantity, unitPrice} = mutation;

    sendDyEvent({
        name,
        properties: {
            dyType,
            value: unitPrice === null ? null : toDyPrice(unitPrice * quantity),
            ...(currency ? {currency} : {}),
            productId,
            quantity,
        },
    });
}
